import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  Plus, Trash2, Edit3, X, Music2, Phone, Mail, Database, AlertTriangle, MapPin,
  Search, Filter, Heart, Globe, Star, BadgeCheck, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import BulkImportButton from "@/components/BulkImportButton";

const ART_CATEGORIES = [
  { key: "all", label: "Tutti" },
  { key: "fotografo", label: "Fotografo" },
  { key: "dronista", label: "Dronista" },
  { key: "mangiafuoco", label: "Mangiafuoco / Fire show" },
  { key: "giocoliere", label: "Giocoliere" },
  { key: "clown", label: "Clown / Mimo / Comicità" },
  { key: "trapezzista", label: "Acrobati / Trapezzisti" },
  { key: "trampolieri", label: "Trampolieri" },
  { key: "circus", label: "Circo / Performance" },
  { key: "mago", label: "Mago / Prestigiatore" },
  { key: "band", label: "Band / Orchestra" },
  { key: "musicista", label: "Musicista (Sax, Violino...)" },
  { key: "street_artist", label: "Artisti di strada / Buskers" },
  { key: "animatore", label: "Animatore / Intrattenitore" },
  { key: "spogliarellista", label: "Burlesque" },
  { key: "dj", label: "DJ" },
];

export const ITALIAN_REGIONS = [
  "Abruzzo", "Basilicata", "Calabria", "Campania", "Emilia-Romagna", "Friuli-Venezia Giulia",
  "Lazio", "Liguria", "Lombardia", "Marche", "Molise", "Piemonte", "Puglia", "Sardegna",
  "Sicilia", "Toscana", "Trentino-Alto Adige", "Umbria", "Valle d'Aosta", "Veneto",
];

const fmtEur = (n) => `€ ${Number(n).toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

export default function Artists() {
  const [items, setItems] = useState([]);
  const [favs, setFavs] = useState(new Set());
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [cat, setCat] = useState("all");
  const [region, setRegion] = useState("all");
  const [city, setCity] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const params = { category: cat };
    if (region !== "all") params.region = region;
    if (city.trim()) params.city = city.trim();
    if (maxPrice) params.max_price = parseFloat(maxPrice);
    if (search.trim()) params.search = search.trim();
    const r = await api.get("/artists", { params });
    setItems(r.data);
  };
  const loadFavs = async () => {
    try {
      const r = await api.get("/favorites/artist");
      setFavs(new Set(r.data));
    } catch { /* ignore */ }
  };
  useEffect(() => { loadFavs(); }, []);
  useEffect(() => {
    api.get("/cms/check-admin").then((r) => setIsSuperAdmin(!!r.data?.is_admin)).catch(() => setIsSuperAdmin(false));
  }, []);
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cat, region, city, maxPrice, search]);

  const visible = favoritesOnly ? items.filter((a) => favs.has(a.id)) : items;

  const toggleFav = async (id) => {
    const next = new Set(favs);
    if (next.has(id)) next.delete(id); else next.add(id);
    setFavs(next);
    try {
      const r = await api.post(`/favorites/artist/${id}`);
      if (r.data.favorited) toast.success("Aggiunto ai preferiti");
      else toast.success("Rimosso dai preferiti");
    } catch (e) {
      setFavs(favs); // rollback
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };
  const bulkSaveVisible = async () => {
    if (visible.length === 0) {
      toast.error("Nessun artista visibile da salvare");
      return;
    }
    try {
      const r = await api.post("/favorites/artist/bulk", { entity_ids: visible.map((a) => a.id) });
      toast.success(`Aggiunti ${r.data.added} ai preferiti (${r.data.already_present} già presenti)`);
      loadFavs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  const handleSave = async (a) => {
    try {
      if (a.id) await api.put(`/artists/${a.id}`, a);
      else await api.post("/artists", a);
      toast.success("Artista salvato nel database condiviso");
      setEditing(null); load();
    } catch { toast.error("Errore"); }
  };
  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare questo artista dal database condiviso?")) return;
    try { await api.delete(`/artists/${id}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const handleReseed = async () => {
    if (!window.confirm("Ricaricare il database con gli artisti standard? (solo super admin)")) return;
    try { await api.post("/artists/seed"); toast.success("Database artisti ricaricato"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const handleClearAll = async () => {
    if (!window.confirm("ATTENZIONE: cancellare TUTTI gli artisti dal database condiviso? (solo super admin)")) return;
    try { await api.delete("/artists"); toast.success("Database artisti svuotato"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const handleToggleVerify = async (id) => {
    try {
      const r = await api.post(`/admin/verify/artist/${id}`);
      toast.success(r.data.verified ? "Profilo verificato ✓" : "Verifica rimossa");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const resetFilters = () => { setCat("all"); setRegion("all"); setCity(""); setMaxPrice(""); setSearch(""); setFavoritesOnly(false); };
  const activeFilters = (cat !== "all" ? 1 : 0) + (region !== "all" ? 1 : 0) + (city ? 1 : 0) + (maxPrice ? 1 : 0) + (search ? 1 : 0) + (favoritesOnly ? 1 : 0);

  return (
    <div className="space-y-6" data-testid="artists-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-[#2563EB] font-semibold font-ui">Database condiviso</p>
          <h1 className="font-display text-4xl md:text-5xl text-[#0F2741] tracking-tight mt-2">Artisti & Fotografi</h1>
          <p className="text-slate-500 mt-2">{visible.length} di {items.length} risultati · {favs.size} preferiti</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={handleClearAll} data-testid="btn-clear-artists" className="btn-ghost border border-rose-200 text-rose-600 hover:bg-rose-50"><AlertTriangle className="w-4 h-4" /> Cancella DB</button>
          <BulkImportButton kind="artists" onImported={load} />
          <button onClick={handleReseed} data-testid="btn-reseed-artists" className="btn-ghost border border-slate-200"><Database className="w-4 h-4" /> Ricarica DB</button>
          <button onClick={() => setEditing({})} data-testid="btn-new-artist" className="btn-primary"><Plus className="w-4 h-4" /> Aggiungi al DB</button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="filters-panel">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <Filter className="w-4 h-4 text-[#2563EB]" />
          <p className="font-ui font-semibold text-sm text-[#0F2741]">Filtra</p>
          {activeFilters > 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-[#EFF6FF] text-[#2563EB] font-semibold">{activeFilters} attivi</span>
          )}
          <button
            onClick={() => setFavoritesOnly((v) => !v)}
            data-testid="btn-toggle-favorites-only"
            className={`ml-auto inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border transition ${favoritesOnly ? "bg-amber-100 text-amber-700 border-amber-200" : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"}`}
          >
            <Star className={`w-3.5 h-3.5 ${favoritesOnly ? "fill-amber-400 text-amber-500" : ""}`} />
            {favoritesOnly ? "Mostra solo preferiti" : "Mostra tutti"}
          </button>
          <button
            onClick={bulkSaveVisible}
            data-testid="btn-save-visible-as-favorites"
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-[#2563EB] text-white hover:bg-[#1D4ED8] transition"
          >
            <Heart className="w-3.5 h-3.5" /> Salva tutti i visibili come preferiti
          </button>
          {activeFilters > 0 && (
            <button onClick={resetFilters} className="text-xs text-slate-400 hover:text-[#2563EB] underline">Reset</button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
            <input data-testid="filter-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca per nome" className="w-full pl-8 pr-3 py-2 rounded-lg border border-slate-200 text-sm" />
          </div>
          <select data-testid="filter-region" value={region} onChange={(e) => setRegion(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white">
            <option value="all">Tutte le regioni</option>
            {ITALIAN_REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <input data-testid="filter-city" value={city} onChange={(e) => setCity(e.target.value)} placeholder="Città" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
          <div className="relative">
            <span className="absolute left-3 top-2 text-slate-400 text-sm">€</span>
            <input data-testid="filter-max-price" type="number" min="0" step="50" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} placeholder="Prezzo max" className="w-full pl-7 pr-3 py-2 rounded-lg border border-slate-200 text-sm" />
          </div>
          <select data-testid="filter-category" value={cat} onChange={(e) => setCat(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white">
            {ART_CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {visible.length === 0 && <div className="col-span-full text-center text-slate-400 py-12">Nessun artista trovato con questi filtri.</div>}
        {visible.map((a) => {
          const isFav = favs.has(a.id);
          return (
            <div key={a.id} className="bg-white rounded-xl border border-slate-200 p-5 card-hover relative" data-testid={`artist-card-${a.id}`}>
              <button
                onClick={() => toggleFav(a.id)}
                data-testid={`btn-fav-artist-${a.id}`}
                className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-slate-50 transition"
                title={isFav ? "Rimuovi dai preferiti" : "Aggiungi ai preferiti"}
              >
                <Heart className={`w-5 h-5 ${isFav ? "fill-rose-500 text-rose-500" : "text-slate-300"}`} />
              </button>
              <div className="flex items-start justify-between pr-8">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-[#2563EB] to-[#0F2741] grid place-items-center text-white"><Music2 className="w-5 h-5" /></div>
                  <div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <p className="font-semibold text-[#0F2741] font-ui">{a.name}</p>
                      {a.verified && (
                        <span title="Profilo verificato dal team Event System 360" className="inline-flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-700 font-semibold">
                          <BadgeCheck className="w-3 h-3" /> Verificato
                        </span>
                      )}
                    </div>
                    <p className="text-xs uppercase tracking-wider text-[#1D4ED8]">{a.category}{a.specialty ? ` · ${a.specialty}` : ""}</p>
                  </div>
                </div>
              </div>
              <div className="mt-3 font-display text-xl text-[#0F2741]">{fmtEur(a.price_eur)}</div>
              {(a.city || a.region) && (
                <p className="text-xs text-slate-500 mt-2 flex items-center gap-1"><MapPin className="w-3 h-3" /> {[a.city, a.region].filter(Boolean).join(" · ")}</p>
              )}
              <p className="text-sm text-slate-500 mt-3 line-clamp-2">{a.description}</p>
              <div className="mt-4 flex flex-wrap gap-3 text-xs text-slate-500">
                {a.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {a.phone}</span>}
                {a.email && <a href={`mailto:${a.email}`} className="inline-flex items-center gap-1 hover:text-[#2563EB]"><Mail className="w-3 h-3" /> {a.email}</a>}
                {a.website && <a href={a.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-[#2563EB]"><Globe className="w-3 h-3" /> sito</a>}
              </div>
              <div className="mt-4 flex gap-2 pt-3 border-t border-slate-100">
                <button onClick={() => setEditing(a)} className="btn-ghost flex-1"><Edit3 className="w-3.5 h-3.5" /> Modifica</button>
                {isSuperAdmin && (
                  <button onClick={() => handleToggleVerify(a.id)} data-testid={`btn-verify-artist-${a.id}`}
                    title={a.verified ? "Rimuovi verifica" : "Verifica profilo"}
                    className={`btn-ghost ${a.verified ? "text-sky-600 border border-sky-200" : "text-slate-500 border border-slate-200"}`}>
                    <ShieldCheck className="w-3.5 h-3.5" />
                  </button>
                )}
                <button onClick={() => handleDelete(a.id)} className="btn-ghost text-rose-500"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          );
        })}
      </div>

      {editing !== null && <ArtistModal initial={editing} onClose={() => setEditing(null)} onSave={handleSave} />}
    </div>
  );
}

function ArtistModal({ initial, onClose, onSave }) {
  const [form, setForm] = useState({
    id: initial.id,
    name: initial.name || "",
    category: initial.category || "dj",
    specialty: initial.specialty || "",
    phone: initial.phone || "",
    email: initial.email || "",
    website: initial.website || "",
    price_eur: initial.price_eur || 0,
    description: initial.description || "",
    address: initial.address || "",
    city: initial.city || "",
    region: initial.region || "",
  });
  return (
    <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={(e) => { e.preventDefault(); onSave({ ...form, price_eur: parseFloat(form.price_eur) || 0 }); }}
        className="bg-white rounded-xl p-6 w-full max-w-lg space-y-3 max-h-[90vh] overflow-y-auto" data-testid="artist-modal">
        <div className="flex justify-between items-center sticky top-0 bg-white pb-2 border-b border-slate-100">
          <h3 className="font-display text-2xl text-[#0F2741]">{form.id ? "Modifica" : "Aggiungi"} artista</h3>
          <button type="button" onClick={onClose}><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <p className="text-xs text-slate-500">Gli artisti sono nel <strong>database condiviso</strong>: ciò che inserisci sarà disponibile per tutti gli organizzatori della piattaforma.</p>
        <input required data-testid="input-artist-name" value={form.name} placeholder="Nome / Nome d'arte" onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-slate-200" />
        <div className="grid grid-cols-2 gap-3">
          <select data-testid="input-artist-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="px-3 py-2 rounded-lg border border-slate-200">
            {ART_CATEGORIES.filter((c) => c.key !== "all").map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
          <input data-testid="input-artist-specialty" value={form.specialty} placeholder="Specialità (es: Luxury Wedding)" onChange={(e) => setForm({ ...form, specialty: e.target.value })} className="px-3 py-2 rounded-lg border border-slate-200" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <input data-testid="input-artist-phone" value={form.phone} placeholder="Telefono" onChange={(e) => setForm({ ...form, phone: e.target.value })} className="px-3 py-2 rounded-lg border border-slate-200" />
          <input data-testid="input-artist-email" value={form.email} placeholder="Email" type="email" onChange={(e) => setForm({ ...form, email: e.target.value })} className="px-3 py-2 rounded-lg border border-slate-200" />
        </div>
        <input data-testid="input-artist-website" value={form.website} placeholder="Sito web (https://...)" onChange={(e) => setForm({ ...form, website: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-slate-200" />
        <input data-testid="input-artist-address" value={form.address} placeholder="Indirizzo" onChange={(e) => setForm({ ...form, address: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-slate-200" />
        <div className="grid grid-cols-2 gap-3">
          <input data-testid="input-artist-city" value={form.city} placeholder="Città" onChange={(e) => setForm({ ...form, city: e.target.value })} className="px-3 py-2 rounded-lg border border-slate-200" />
          <select data-testid="input-artist-region" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white">
            <option value="">Regione</option>
            {ITALIAN_REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <label className="block">
          <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Prezzo medio per evento (€)</span>
          <input data-testid="input-artist-price" type="number" step="10" min="0" value={form.price_eur} onChange={(e) => setForm({ ...form, price_eur: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-slate-200 mt-1 font-semibold" />
        </label>
        <textarea data-testid="input-artist-description" value={form.description} placeholder="Descrizione del servizio, repertorio, durata tipica..." rows={3} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-slate-200" />
        <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
          <button type="button" onClick={onClose} className="btn-ghost">Annulla</button>
          <button type="submit" data-testid="btn-save-artist" className="btn-primary">Salva nel DB condiviso</button>
        </div>
      </form>
    </div>
  );
}
